/* ============================================================================
   Messages: short notes between a person and their agents (COPL-44,
   migrations/0025_messages.sql).
   ----------------------------------------------------------------------------
   Who may message whom, the one rule both the Worker and the checks read:
     - a person may message their own agents;
     - an agent may message its owner;
     - anyone else on a board an agent is on may message it when its owner
       opened it to the board's members (agents.work_from = 'members'), and
       those messages are untrusted, like comments;
     - a reply goes back to whoever sent the message it answers, whoever
       they are, so an agent can answer a member it was allowed to hear from;
     - nobody messages themselves, agents never message other agents (not
       even their owner's other agents) and people never message people:
       between people, comments on a task are the way.
   A message is trusted when it comes from the recipient's owner, or, for a
   person, from one of their own agents.
   ========================================================================== */

/** The most a message holds. A message is a nudge, not a document. */
export const MESSAGE_MAX = 1000;

export interface MessageParty {
  id: string;
  kind: "person" | "agent";
  /** For an agent: the person it belongs to. */
  ownerId: string | null;
  /** For an agent: who may give it work, and so message it. */
  workFrom?: "owner" | "members";
}

export type MessageVerdict = { ok: true; trusted: boolean } | { ok: false; reason: string };

/** Whether the owner relation joins the two, either way round. */
export const ownerRelated = (a: MessageParty, b: MessageParty) => a.ownerId === b.id || b.ownerId === a.id;

/**
 * May `sender` message `recipient`? `sharesBoard` is whether they are both on
 * a board the agent can see; `reply` whether this answers a message
 * `recipient` sent `sender`.
 */
export function mayMessage(
  sender: MessageParty,
  recipient: MessageParty,
  { sharesBoard = false, reply = false }: { sharesBoard?: boolean; reply?: boolean } = {},
): MessageVerdict {
  const trusted = ownerRelated(sender, recipient);
  if (sender.id === recipient.id) return { ok: false, reason: "You cannot message yourself" };
  if (sender.kind === "agent" && recipient.kind === "agent") return { ok: false, reason: "Agents cannot message other agents" };
  if (reply) return { ok: true, trusted };
  if (sender.kind === "person" && recipient.kind === "person") {
    return { ok: false, reason: "Messages go between a person and their agents; between people, comment on a task" };
  }
  if (trusted) return { ok: true, trusted };
  if (sender.kind === "agent") return { ok: false, reason: "An agent can message only its owner, or answer a message it was sent" };
  if (recipient.workFrom === "members" && sharesBoard) return { ok: true, trusted: false };
  return {
    ok: false,
    reason:
      recipient.workFrom === "members"
        ? "You can message this agent only while you share a board with it"
        : "Only this agent's owner can message it",
  };
}

/**
 * Whom the nudge pane offers (COPL-115, GET /api/messages/recipients): the
 * agents among `candidates` that `viewer` may message by mayMessage, their
 * own first, then by handle. `sharesBoard` is the same fact postMessage
 * asks for. Recipients are agents only: between people, comments.
 */
export function recipientsOf<C extends { party: MessageParty; handle: string; sharesBoard: boolean }>(
  viewer: MessageParty,
  candidates: C[],
): C[] {
  const theirs = (c: C) => Number(c.party.ownerId !== viewer.id);
  return candidates
    .filter((c) => c.party.kind === "agent" && mayMessage(viewer, c.party, { sharesBoard: c.sharesBoard }).ok)
    .sort((a, b) => theirs(a) - theirs(b) || a.handle.localeCompare(b.handle));
}
